import {
    IconBolt,
    IconChevronRight,
    IconClockHour4,
    IconDots,
    IconLink,
    IconSettings,
    IconShare,
    IconX,
} from "@tabler/icons-react";
import { Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Button, Menu, StatusChip, Text } from "@fragments";
import { CommunityBanner, UserAvatar } from "@components/Community";
import { isProfileLinkable, profileLinkProps } from "@helpers/profile";

import PostActions from "@components/Feed/components/PostActions";

/**
 * 02.A — publisher, then (hung off the avatar by an L-shaped connector) the
 * community and group it was posted in, the text, and the action row parted
 * from it by a side-to-side hairline.
 */
export default function PostInfo({
    post,
    loading,
    isAdmin,
    reacting,
    titleId,
    onClose,
    onReact,
    onComment,
    onShare,
    onCopyLink,
    onOpenAdminOptions,
}) {
    const { t } = useTranslation("groupModals");

    const publisher = post?.publisher;
    const points = Number(post?.insights?.points || 0);
    const group = post?.group;

    const menuItems = [
        { label: t("common:actions.share"), icon: <IconShare />, onClick: onShare },
        { label: t("postModal.copyLink"), icon: <IconLink />, onClick: onCopyLink },
        isAdmin && { divider: true },
        isAdmin && {
            label: t("postModal.manage"),
            icon: <IconSettings />,
            onClick: onOpenAdminOptions,
        },
    ];

    return (
        <section className="post-modal__panel post-info">
            <div className="post-info__top">
                <header className="post-info__head">
                    {loading ? (
                        <div className="post-info__publisher">
                            <Skeleton variant="circular" width={40} height={40} className="pm-skeleton" />
                            <div className="post-info__publisher-text">
                                <Skeleton variant="text" width={140} height={18} className="pm-skeleton" />
                                <Skeleton variant="text" width={90} height={14} className="pm-skeleton" />
                            </div>
                        </div>
                    ) : (
                        <div
                            className={`post-info__publisher${isProfileLinkable(publisher) ? " post-info__publisher--link" : ""}`}
                            {...profileLinkProps(publisher)}
                        >
                            <UserAvatar user={publisher} size={40} link={false} />
                            <div className="post-info__publisher-text">
                                <Text.label contrast={10} className="post-info__name" id={titleId}>
                                    {publisher?.username}
                                </Text.label>
                                {post?.scheduled_to ? (
                                    <StatusChip.Info
                                        className="post-info__scheduled"
                                        icon={<IconClockHour4 />}
                                        preserveCase
                                        text={t("community:feed.scheduledTo", {
                                            date: window.helpers.pretty.date(post.scheduled_to),
                                        })}
                                    />
                                ) : (
                                    <Text.caption contrast={6}>
                                        {window.helpers.pretty.date(post?.timestamps?.published)}
                                    </Text.caption>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="post-info__controls">
                        {!loading && (
                            <Menu items={menuItems} placement="bottom-end">
                                <Button
                                    variant="text"
                                    color="inverse"
                                    size="small"
                                    icon={<IconDots />}
                                    tooltip={t("postModal.options")}
                                    aria-label={t("postModal.options")}
                                    preventLoading
                                />
                            </Menu>
                        )}
                        <Button
                            variant="text"
                            color="inverse"
                            size="small"
                            icon={<IconX />}
                            onClick={onClose}
                            tooltip={t("postModal.close")}
                            aria-label={t("postModal.close")}
                            preventLoading
                        />
                    </div>
                </header>

                {/* Where: an "L" from the avatar down to the community, then the group. */}
                <div className="post-info__where">
                    <span className="post-info__connector" aria-hidden="true" />
                    {loading ? (
                        <>
                            <Skeleton variant="rounded" width={28} height={28} className="pm-skeleton" />
                            <Skeleton variant="text" width={140} height={16} className="pm-skeleton" />
                        </>
                    ) : (
                        <>
                            <div className="post-info__place">
                                <CommunityBanner banner={post?.community?.banner} size={28} />
                                <Text.span contrast={8} className="post-info__place-name">
                                    {post?.community?.name}
                                </Text.span>
                            </div>
                            <IconChevronRight style={{width: '15px', height: '15px'}} />
                            {group?.name && (
                                <div className="post-info__place">
                                    <CommunityBanner banner={group.banner} size={28} />
                                    <Text.span contrast={8} className="post-info__place-name">
                                        {group.name}
                                    </Text.span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="post-info__text">
                    <Skeleton variant="text" width="100%" height={16} className="pm-skeleton" />
                    <Skeleton variant="text" width="82%" height={16} className="pm-skeleton" />
                    <Skeleton variant="text" width="60%" height={16} className="pm-skeleton" />
                </div>
            ) : (
                post?.text && (
                    <div className="post-info__text">
                        <Text.span contrast={9} weight={400}>{post.text}</Text.span>
                    </div>
                )
            )}

            <hr className="post-modal__rule" />

            <div className="post-info__footer">
                <PostActions
                    post={post}
                    busy={loading || reacting}
                    onReact={onReact}
                    onComment={onComment}
                    onShare={onShare}
                />
                {points > 0 && (
                    <Text.caption contrast={6} left={<IconBolt />}>
                        {t("postModal.points", { value: points })}
                    </Text.caption>
                )}
            </div>
        </section>
    );
}
